import { startOfDay, endOfDay } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { DateRangePicker } from '@/components/ui/DateRangePicker';
import {
  type TimelinePreset,
  getSpanRange,
  stepRange,
  toDateInputValue,
  daysInPeriod,
  formatPeriodLabel,
} from '@/utils/statusUpdatePeriod';

/**
 * TimelineControls
 *
 * Navigation bar for the Brand Portfolio Timeline: span presets (2 weeks /
 * month / quarter / custom), step arrows, a "Today" re-anchor button, and a
 * custom date-range picker. Fully controlled — the parent owns `value` and
 * drives data fetching/persistence off the emitted `{from, to, preset}`.
 */

/** Custom range span cap, mirrors StatusUpdateComposeModal's MAX_CUSTOM_RANGE_DAYS. */
const MAX_CUSTOM_RANGE_DAYS = 92;

export interface TimelineRange {
  from: Date;
  to: Date;
  preset: TimelinePreset;
}

interface TimelineControlsProps {
  value: TimelineRange;
  onChange: (next: TimelineRange) => void;
  /** Account timezone cascade (user → team → default) from `useTimezone()`.
   *  All preset/step boundary math is anchored against THIS calendar, not
   *  the browser engine's local timezone, so it matches the day-bucket keys
   *  the Timeline grid renders. */
  timezone: string;
  className?: string;
}

const PRESET_OPTIONS: { id: TimelinePreset; labelKey: string; defaultLabel: string }[] = [
  { id: '2w', labelKey: 'timeline_preset_2w', defaultLabel: '2 weeks' },
  { id: 'month', labelKey: 'timeline_preset_month', defaultLabel: 'Month' },
  { id: 'quarter', labelKey: 'timeline_preset_quarter', defaultLabel: 'Quarter' },
  { id: 'custom', labelKey: 'timeline_preset_custom', defaultLabel: 'Custom' },
];

export const TimelineControls = ({
  value,
  onChange,
  timezone,
  className = '',
}: TimelineControlsProps) => {
  // 'agency' ns gives us the existing status_compose_week_of/_week_number
  // strings (already translated in all 5 locales) for the range label; the
  // i18n config's `fallbackNS: 'common'` transparently resolves the new
  // timeline_* keys declared in common.json.
  const { t } = useTranslation('agency');
  const { from, to, preset } = value;

  // Raw "now" — `getSpanRange`/`stepRange` derive the account-timezone
  // calendar day from this instant internally, so no local `startOfDay`
  // pre-snap is needed (or correct) here.
  const today = new Date();

  const handlePresetClick = (nextPreset: TimelinePreset) => {
    if (nextPreset === 'custom') {
      // Reveal the range picker but keep the currently visible window as
      // its initial value — no data jump on switching modes.
      onChange({ from, to, preset: 'custom' });
      return;
    }
    onChange({ ...getSpanRange(nextPreset, today, timezone), preset: nextPreset });
  };

  const handlePrev = () => {
    onChange({ ...stepRange(preset, { from, to }, -1, timezone), preset });
  };

  const handleNext = () => {
    onChange({ ...stepRange(preset, { from, to }, 1, timezone), preset });
  };

  const handleToday = () => {
    onChange({
      ...getSpanRange(preset === 'custom' ? '2w' : preset, today, timezone),
      preset,
    });
  };

  const handleCustomRangeChange = (range: { from: Date | null; to: Date | null }) => {
    if (!range.from || !range.to) return;
    onChange({ from: startOfDay(range.from), to: endOfDay(range.to), preset: 'custom' });
  };

  const customDays =
    preset === 'custom' ? daysInPeriod(toDateInputValue(from), toDateInputValue(to)) : 0;
  const customRangeTooLong = preset === 'custom' && customDays > MAX_CUSTOM_RANGE_DAYS;

  const currentRangeLabel = formatPeriodLabel(
    toDateInputValue(from),
    toDateInputValue(to),
    t('status_compose_week_of', { defaultValue: 'Week of' }),
    (week) => t('status_compose_week_number', { week, defaultValue: `KW ${week}` }),
  );

  // Week mode: the Timeline grid aggregates each column into a whole ISO
  // week (Mon–Sun) once the visible span exceeds 45 days (e.g. the Quarter
  // preset, or a long custom range). This `> 45` threshold MUST stay in
  // sync with `useTimelineWindow` in AgencyPortfolio.tsx.
  const isWeekMode = daysInPeriod(toDateInputValue(from), toDateInputValue(to)) > 45;

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      {/* Step + segmented presets */}
      <div className="flex items-center gap-1">
        <button
          type="button"
          className="w-6 h-6 inline-flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors outline-none border border-transparent focus-visible:border-primary-600"
          onClick={handlePrev}
          aria-label={t('timeline_nav_prev', { defaultValue: 'Previous' })}
        >
          <i
            className="hm hm-angle-left block leading-none text-[10px]"
            aria-hidden="true"
          />
        </button>

        <div
          className="inline-flex items-center rounded-full bg-gray-100 p-0.5 text-xs font-semibold"
          role="radiogroup"
          aria-label={t('timeline_preset_2w', { defaultValue: '2 weeks' })}
        >
          {PRESET_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              role="radio"
              aria-checked={preset === opt.id}
              className={`px-[9px] py-[3px] rounded-full transition-colors outline-none border border-transparent focus-visible:border-primary-600 ${
                preset === opt.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => handlePresetClick(opt.id)}
            >
              {t(opt.labelKey, { defaultValue: opt.defaultLabel })}
            </button>
          ))}
        </div>

        <button
          type="button"
          className="w-6 h-6 inline-flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors outline-none border border-transparent focus-visible:border-primary-600"
          onClick={handleNext}
          aria-label={t('timeline_nav_next', { defaultValue: 'Next' })}
        >
          <i
            className="hm hm-angle-right block leading-none text-[10px]"
            aria-hidden="true"
          />
        </button>
      </div>

      <button
        type="button"
        className="px-2.5 py-1 text-xs font-medium text-gray-600 border border-gray-200 rounded-full hover:border-gray-300 hover:text-gray-900 transition-colors outline-none focus-visible:border-primary-600"
        onClick={handleToday}
      >
        {t('timeline_nav_today', { defaultValue: 'Today' })}
      </button>

      {isWeekMode && (
        <span className="text-[10px] text-gray-400">
          {t('timeline_week_mode_hint', { defaultValue: 'Weekly view — each square is one week (Mon–Sun)' })}
        </span>
      )}

      {preset === 'custom' ? (
        <div className="flex items-center gap-2">
          <DateRangePicker
            value={{ from, to }}
            onChange={handleCustomRangeChange}
            size="sm"
            className="w-56"
          />
          <span className="text-xs text-gray-400 whitespace-nowrap">{currentRangeLabel}</span>
          {customRangeTooLong && (
            <span className="text-[11px] text-coral-600" role="alert">
              {t('timeline_range_too_long', { defaultValue: "Range can't exceed 3 months" })}
            </span>
          )}
        </div>
      ) : (
        <span className="text-xs text-gray-400 whitespace-nowrap">{currentRangeLabel}</span>
      )}
    </div>
  );
};
